import React, { Suspense, useState, useEffect, lazy } from 'react';
import { usePlans } from '@/context/PlansContext';
import useActionStore from '@/store/Actions';
import ChatsSidebar from '../ChatsSidebar/ChatsSidebar';
import { Outlet, useNavigate } from 'react-router-dom';
import LoadingComponent from '@/components/LoadingComponent';
import { ROUTES } from '@/routes';
import useAuthStore from '@/store/Auth';
import NotFound from '@/pages/notFound/NotFound';
import KycForm from '@/components/modals/kycForm';
import WelcomeModal from '@/components/modals/Onboarding/WelcomeModal';
import MobileScreen from '../MobileScreen/MobileScreen';
import Sidebar from '@/components/common/Sidebar/Sidebar';
import Heading from '@/components/common/Heading/Heading';
import { useModal } from '@/context/ModalContext';
import useDashboardInit from '@/hooks/useDashboardInit';
import useRouteValidation from '@/hooks/useRouteValidation';
import useMobileDetect from '@/hooks/useMobileDetect';
import useCreditCheck from '@/hooks/useCreditCheck';
import CreateNewIntake from '@/components/modals/CreateNewIntake/CreateNewIntake';
import IntakeModal from '@/components/modals/IntakeModal/IntakeModal';
import { JIRA_IMPORT_DETAILS_MODAL_ID } from '@/components/modals/JiraImportDetailsModal/constants';

// Lazy-loaded modals — only fetched when their open state becomes true
const TaskModal = lazy(() => import('@/components/modals/TaskModal/TaskModal'));
const CreateNewTask = lazy(
  () => import('@/components/modals/CreateNewTask/CreateNewTask')
);
const SubscriptionWarningModal = lazy(
  () =>
    import('@/components/modals/SubscriptionWarning/SubscriptionWarningModal')
);
const SlackSuccessModal = lazy(
  () => import('@/components/modals/SlackSuccessModal/SlackSuccessModal')
);
const IntegrationModal = lazy(
  () =>
    import('@/components/modals/IntegrationModals/IntegrationModal/IntegrationModal')
);
const LogoutModal = lazy(
  () => import('@/components/modals/LogoutModal/LogoutModal')
);
const CreateProjectModal = lazy(
  () => import('@/components/modals/CreateProjectModal/CreateProjectModal')
);
const CreateNewTeamModal = lazy(
  () => import('@/components/modals/CreateNewTeamModal/CreateNewTeamModal')
);
const PriorityLevelsModal = lazy(
  () => import('@/components/modals/InfoModals/PriorityLevelsModal')
);
const LeaveTeamModal = lazy(
  () => import('@/components/modals/LeaveTeamModal/LeaveTeamModal')
);
const TransferOwnershipModal = lazy(
  () =>
    import('@/components/modals/TransferOwnershipModal/TransferOwnershipModal')
);
const TransferProjectOwnershipModal = lazy(
  () =>
    import('@/components/modals/TransferProjectOwnershipModal/TransferProjectOwnershipModal')
);
const CreateProjectErrorModal = lazy(
  () =>
    import('@/components/modals/CreateProjectErrorModal/CreateProjectErrorModal')
);
const DeleteProjectModal = lazy(
  () => import('@/components/modals/DeleteProjectModal/DeleteProjectModal')
);
const JiraImportDetailsModal = lazy(
  () =>
    import('@/components/modals/JiraImportDetailsModal/JiraImportDetailsModal')
);

export default function DashboardLayout() {
  const {
    collapsed,
    setCollapsed,
    chatCollapsed,
    toggleSubscriptionWarning,
    taskModal,
    createTaskModal,
    subscriptionWarning,
    slackSuccess,
    integrationModal,
    logoutModal,
    createNewTeamModal,
  } = useActionStore();
  const navigate = useNavigate();
  const { isOpen: isModalOpen, modalData } = useModal();
  const { user, isAuthenticated } = useAuthStore();
  const { isSubscribed, hasSubscriptionEnded } = usePlans();

  // ── Hooks ──
  const isMobile = useMobileDetect();
  const { loading } = useDashboardInit(isMobile);
  const { isExistingRoute, isInsightsPage } = useRouteValidation();
  useCreditCheck(isMobile);

  // ── Auto-collapse sidebar below xl (1280px) ──
  useEffect(() => {
    const autoCollapse = () => {
      if (window.innerWidth < 1280) {
        setCollapsed(true);
      }
    };
    autoCollapse();
    window.addEventListener('resize', autoCollapse);
    return () => window.removeEventListener('resize', autoCollapse);
  }, []);

  // ── KYC / Subscription warning ──
  const [showWelcome, setShowWelcome] = useState(false);
  const [showKYCForm, setShowKYCForm] = useState(false);
  const [openPendingProjects, setOpenPendingProjects] = useState(false);

  // Show Welcome modal first for new users; KYC form opens from there
  useEffect(() => {
    if (isAuthenticated && !isMobile && user?.isNew) {
      setShowWelcome(true);
    }
  }, [
    isAuthenticated,
    isMobile,
    user?.isNew,
    isSubscribed,
    hasSubscriptionEnded,
  ]);

  const handleWelcomeContinue = () => {
    setShowWelcome(false);
    setShowKYCForm(true);
  };

  const handleWelcomeDismiss = () => {
    setShowWelcome(false);
  };

  const closeKYCForm = () => {
    setShowKYCForm(false);
    if (!isSubscribed) {
      // toggleSubscriptionWarning(true);
    }
  };

  if (loading) {
    return (
      <div className="h-screen bg-frame-main">
        <LoadingComponent />
      </div>
    );
  }

  if (isMobile) {
    return <MobileScreen />;
  }

  return (
    <>
      {isExistingRoute ? (
        <div className="relative bg-frame-main text-text-primary-body h-screen w-full flex flex-col">
          <div className="flex flex-1 overflow-hidden">
            {/* Sidebar */}
            <div
              className={`${
                collapsed ? 'w-[72px]' : 'w-[250px]'
              } flex-shrink-0 transition-[width] duration-300 h-full`}
            >
              <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>
            {/* Main Content */}
            <div className={`flex-1 overflow-hidden h-full`}>
              <Outlet />
            </div>

            {/* Chats Sidebar */}
            {isInsightsPage && (
              <div
                className={`${
                  chatCollapsed
                    ? 'md:w-[5%] lg:w-[5%] xl:w-[4%]'
                    : 'md:w-[25%] lg:w-[25%] xl:w-[22%]'
                } transition-width duration-500 ease-in-out h-full`}
              >
                <ChatsSidebar />
              </div>
            )}
          </div>
        </div>
      ) : (
        <NotFound />
      )}
      {showWelcome && (
        <WelcomeModal
          onContinue={handleWelcomeContinue}
          onDismiss={handleWelcomeDismiss}
        />
      )}
      {showKYCForm && <KycForm onClose={closeKYCForm} />}

      {/* Lazy-loaded modals — only mounted & fetched when their open state is true */}
      <Suspense fallback={null}>
        {taskModal && <TaskModal />}
        {isModalOpen('intake-details') && <IntakeModal />}
        {createTaskModal && <CreateNewTask />}
        {subscriptionWarning && <SubscriptionWarningModal />}
        {slackSuccess && <SlackSuccessModal />}
        {integrationModal && <IntegrationModal />}
        {logoutModal && <LogoutModal />}
        {isModalOpen('create-new-intake') && <CreateNewIntake />}
        {isModalOpen('create-project') && <CreateProjectModal />}
        {isModalOpen('create-project-error') && <CreateProjectErrorModal />}
        {isModalOpen(JIRA_IMPORT_DETAILS_MODAL_ID) && (
          <JiraImportDetailsModal />
        )}
        {createNewTeamModal && <CreateNewTeamModal />}
        {isModalOpen('priority-levels') && <PriorityLevelsModal />}
        {isModalOpen('leave-team') && (
          <LeaveTeamModal onLeave={() => navigate(ROUTES.TEAMS)} />
        )}
        {isModalOpen('transfer-ownership') && (
          <TransferOwnershipModal onTransfer={modalData?.onTransfer} />
        )}
        {isModalOpen('transfer-project-ownership') && (
          <TransferProjectOwnershipModal onTransfer={modalData?.onTransfer} />
        )}
        {isModalOpen('delete-project') && <DeleteProjectModal />}
      </Suspense>
    </>
  );
}
